import { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { usersAPI } from '@/shared/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@travellegacy/nexops-design-system';
import { Users as UsersIcon, Plus, Pencil, Trash2, Search, Eye } from 'lucide-react';
import { formatDate } from '@/shared/lib/utils';
import type { User, UserRole } from '@/shared/types';
import { Badge } from '@travellegacy/nexops-design-system';
import { Button } from '@travellegacy/nexops-design-system';
import { Input } from '@travellegacy/nexops-design-system';
import { Pagination } from '@travellegacy/nexops-design-system';
import { TabFilter } from '@travellegacy/nexops-design-system';
import { UserModal } from '../components/UserModal';
import { ConfirmDialog } from '@travellegacy/nexops-design-system';
import { toast } from '@/shared/lib/toast';
import { useAuth } from '@/shared/contexts/AuthContext';
import { isAxiosError } from 'axios';
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
  SortableTable,
} from '@travellegacy/nexops-design-system';

export default function Users() {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [userToDelete, setUserToDelete] = useState<User | null>(null);
  const [roleFilter, setRoleFilter] = useState<UserRole | 'all'>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(1);
  const pageSize = 20;
  const queryClient = useQueryClient();
  const { impersonate } = useAuth();

  const { data, isLoading, error } = useQuery({
    queryKey: ['users'],
    queryFn: async () => {
      const response = await usersAPI.list();
      return response.data.data as User[];
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (userId: string) => usersAPI.delete(userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      toast.success('Sucesso', 'Usuário deletado com sucesso!');
    },
    onError: (error) => {
      const errorMessage = isAxiosError(error)
        ? error.response?.data?.error?.message || 'Erro ao deletar usuário'
        : 'Erro desconhecido';
      toast.error('Erro', errorMessage);
    },
  });

  const handleNewUser = () => {
    setSelectedUser(null);
    setIsModalOpen(true);
  };

  const handleEditUser = (user: User) => {
    setSelectedUser(user);
    setIsModalOpen(true);
  };

  const handleDeleteUser = (user: User) => {
    setUserToDelete(user);
  };

  const handleConfirmDelete = () => {
    if (userToDelete) {
      deleteMutation.mutate(userToDelete.id);
      setUserToDelete(null);
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedUser(null);
  };

  const handleImpersonate = async (user: User) => {
    try {
      await impersonate(user.id);
      toast.success('Sucesso', `Visualizando como ${user.name}`);
    } catch (error) {
      const errorMessage = isAxiosError(error)
        ? error.response?.data?.error?.message || 'Erro ao acessar dashboard do cliente'
        : 'Erro desconhecido';
      toast.error('Erro', errorMessage);
    }
  };

  // Filter and search users
  const filteredUsers = useMemo(() => {
    if (!data) return [];

    let filtered = data;

    // Filter by role
    if (roleFilter !== 'all') {
      filtered = filtered.filter((user) => user.role === roleFilter);
    }

    // Filter by search query
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (user) =>
          user.name.toLowerCase().includes(query) || user.email.toLowerCase().includes(query)
      );
    }

    return filtered;
  }, [data, roleFilter, searchQuery]);

  // Paginate filtered users
  const totalPages = Math.ceil(filteredUsers.length / pageSize);

  // Reset page when filters change
  const handleRoleFilterChange = (newFilter: UserRole | 'all') => {
    setRoleFilter(newFilter);
    setPage(1);
  };

  const handleSearchChange = (newSearch: string) => {
    setSearchQuery(newSearch);
    setPage(1);
  };

  // Calculate counts for tabs
  const roleCounts = useMemo(() => {
    if (!data) return { all: 0, ADMIN: 0, GESTOR: 0, COLLABORATOR: 0, CLIENT: 0 };

    return {
      all: data.length,
      ADMIN: data.filter((u) => u.role === 'ADMIN').length,
      GESTOR: data.filter((u) => u.role === 'GESTOR').length,
      COLLABORATOR: data.filter((u) => u.role === 'COLLABORATOR').length,
      CLIENT: data.filter((u) => u.role === 'CLIENT').length,
    };
  }, [data]);

  // Tabs configuration
  const roleTabs = [
    { value: 'all', label: 'Todos', count: roleCounts.all, color: 'primary' as const },
    { value: 'ADMIN', label: 'Administradores', count: roleCounts.ADMIN, color: 'red' as const },
    { value: 'GESTOR', label: 'Gestores', count: roleCounts.GESTOR, color: 'blue' as const },
    { value: 'COLLABORATOR', label: 'Colaboradores', count: roleCounts.COLLABORATOR, color: 'gray' as const },
    { value: 'CLIENT', label: 'Clientes', count: roleCounts.CLIENT, color: 'green' as const },
  ];

  const getRoleBadgeColor = (role: string) => {
    switch (role) {
      case 'ADMIN':
        return 'bg-red-500/10 text-red-500 border-red-500/20';
      case 'GESTOR':
        return 'bg-blue-500/10 text-blue-500 border-blue-500/20';
      case 'COLLABORATOR':
        return 'bg-yellow-500/10 text-yellow-600 border-yellow-500/20';
      case 'CLIENT':
        return 'bg-green-500/10 text-green-500 border-green-500/20';
      default:
        return 'bg-gray-500/10 text-gray-500 border-gray-500/20';
    }
  };

  const getStatusBadgeColor = (isActive: boolean) => {
    return isActive
      ? 'bg-green-500/10 text-green-500 border-green-500/20'
      : 'bg-gray-500/10 text-gray-500 border-gray-500/20';
  };

  return (
    <div className="space-y-4 sm:space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-foreground">Usuários</h1>
          <p className="text-sm sm:text-base text-muted-foreground mt-1 sm:mt-2">
            Gerencie todos os usuários do sistema
          </p>
        </div>
        <Button onClick={handleNewUser} className="w-full sm:w-auto">
          <Plus className="h-4 w-4 mr-2" />
          Novo Usuário
        </Button>
      </div>

      <UserModal isOpen={isModalOpen} onClose={handleCloseModal} user={selectedUser} />

      <ConfirmDialog
        open={!!userToDelete}
        onOpenChange={(open) => !open && setUserToDelete(null)}
        title="Deletar Usuário"
        description={`Tem certeza que deseja deletar o usuário "${userToDelete?.name}"? Esta ação não pode ser desfeita.`}
        confirmText="Deletar"
        cancelText="Cancelar"
        onConfirm={handleConfirmDelete}
        variant="destructive"
        icon="delete"
        isLoading={deleteMutation.isPending}
      />

      <Card>
        <CardHeader>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
            <CardTitle className="flex items-center space-x-2">
              <UsersIcon className="h-5 w-5" />
              <span>Lista de Usuários</span>
            </CardTitle>
            <div className="relative w-full sm:w-64">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                type="text"
                placeholder="Buscar por nome ou email..."
                value={searchQuery}
                onChange={(e) => handleSearchChange(e.target.value)}
                className="pl-9"
              />
            </div>
          </div>

          {/* Role Tabs */}
          <TabFilter
            tabs={roleTabs}
            currentTab={roleFilter}
            onChange={(value) => handleRoleFilterChange(value as UserRole | 'all')}
          />
        </CardHeader>
        <CardContent>
          {isLoading && (
            <div className="space-y-3">
              {[...Array(8)].map((_, i) => (
                <div
                  key={i}
                  className="flex items-center justify-between p-4 border rounded-lg animate-pulse"
                >
                  <div className="flex items-center space-x-4 flex-1">
                    <div className="h-10 w-10 bg-muted rounded-full" />
                    <div className="flex-1 space-y-2">
                      <div className="h-4 w-48 bg-muted rounded" />
                      <div className="h-3 w-64 bg-muted rounded" />
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className="h-6 w-16 bg-muted rounded-full" />
                    <div className="h-6 w-16 bg-muted rounded-full" />
                    <div className="h-8 w-8 bg-muted rounded" />
                    <div className="h-8 w-8 bg-muted rounded" />
                  </div>
                </div>
              ))}
            </div>
          )}

          {error && (
            <div className="flex items-center justify-center py-8">
              <p className="text-red-500">Erro ao carregar usuários</p>
            </div>
          )}

          {filteredUsers.length === 0 && !isLoading && !error && (
            <div className="flex items-center justify-center py-8">
              <p className="text-muted-foreground">
                {searchQuery || roleFilter !== 'all'
                  ? 'Nenhum usuário encontrado com os filtros aplicados'
                  : 'Nenhum usuário encontrado'}
              </p>
            </div>
          )}

          {filteredUsers.length > 0 && (
            <>
              <div className="-mx-4 sm:mx-0">
                <SortableTable data={filteredUsers}>
                  {(sortedData) => {
                    const paginatedData = sortedData.slice((page - 1) * pageSize, page * pageSize);
                    return (
                      <Table className="min-w-[640px]">
                        <TableHeader>
                          <TableRow>
                            <TableHead
                              sortable
                              sortKey="name"
                              className="py-3 px-2 sm:px-4 text-xs sm:text-sm"
                            >
                              Nome
                            </TableHead>
                            <TableHead
                              sortable
                              sortKey="email"
                              className="py-3 px-2 sm:px-4 text-xs sm:text-sm hidden md:table-cell"
                            >
                              Email
                            </TableHead>
                            <TableHead
                              sortable
                              sortKey="role"
                              className="py-3 px-2 sm:px-4 text-xs sm:text-sm"
                            >
                              Função
                            </TableHead>
                            <TableHead
                              sortable
                              sortKey="isActive"
                              className="py-3 px-2 sm:px-4 text-xs sm:text-sm hidden lg:table-cell"
                            >
                              Status
                            </TableHead>
                            <TableHead
                              sortable
                              sortKey="createdAt"
                              className="py-3 px-2 sm:px-4 text-xs sm:text-sm hidden xl:table-cell"
                            >
                              Criado em
                            </TableHead>
                            <TableHead
                              sortable
                              sortKey="lastAccessAt"
                              className="py-3 px-2 sm:px-4 text-xs sm:text-sm hidden xl:table-cell"
                            >
                              Último Acesso
                            </TableHead>
                            <TableHead className="text-right py-3 px-2 sm:px-4 text-xs sm:text-sm">
                              Ações
                            </TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {paginatedData.map((user) => (
                            <TableRow key={user.id}>
                              <TableCell className="py-3 px-2 sm:px-4">
                                <div className="font-medium text-foreground text-sm">
                                  {user.name}
                                </div>
                                <div className="text-xs text-muted-foreground md:hidden">
                                  {user.email}
                                </div>
                              </TableCell>
                              <TableCell className="py-3 px-2 sm:px-4 hidden md:table-cell">
                                <div className="text-sm text-muted-foreground">{user.email}</div>
                              </TableCell>
                              <TableCell className="py-3 px-2 sm:px-4">
                                <Badge className={getRoleBadgeColor(user.role)}>{user.role}</Badge>
                              </TableCell>
                              <TableCell className="py-3 px-2 sm:px-4 hidden lg:table-cell">
                                <Badge className={getStatusBadgeColor(user.isActive)}>
                                  {user.isActive ? 'Ativo' : 'Inativo'}
                                </Badge>
                              </TableCell>
                              <TableCell className="py-3 px-2 sm:px-4 hidden xl:table-cell">
                                <div className="text-sm text-muted-foreground">
                                  {formatDate(user.createdAt)}
                                </div>
                              </TableCell>
                              <TableCell className="py-3 px-2 sm:px-4 hidden xl:table-cell">
                                <div className="text-sm text-muted-foreground">
                                  {user.lastAccessAt ? formatDate(user.lastAccessAt) : 'Nunca'}
                                </div>
                              </TableCell>
                              <TableCell className="py-3 px-2 sm:px-4">
                                <div className="flex items-center justify-end space-x-1 sm:space-x-2">
                                  {user.role === 'CLIENT' && (
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      onClick={() => handleImpersonate(user)}
                                      className="h-8 w-8 p-0"
                                      title="Ver Dashboard do Cliente"
                                    >
                                      <Eye className="h-3 w-3 sm:h-4 sm:w-4" />
                                    </Button>
                                  )}
                                  <Button
                                    variant="secondary"
                                    size="sm"
                                    onClick={() => handleEditUser(user)}
                                    className="h-8 w-8 p-0"
                                  >
                                    <Pencil className="h-3 w-3 sm:h-4 sm:w-4" />
                                  </Button>
                                  <Button
                                    variant="destructive"
                                    size="sm"
                                    onClick={() => handleDeleteUser(user)}
                                    disabled={deleteMutation.isPending}
                                    className="h-8 w-8 p-0"
                                  >
                                    <Trash2 className="h-3 w-3 sm:h-4 sm:w-4" />
                                  </Button>
                                </div>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    );
                  }}
                </SortableTable>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <Pagination
                  currentPage={page}
                  totalPages={totalPages}
                  pageSize={pageSize}
                  totalItems={filteredUsers.length}
                  onPageChange={setPage}
                />
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
